import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for tour availability blasts (research/tours-painting —
 * "Send availability"). The rep paints open windows on their own calendar
 * and blasts them to several property owners/brokers; each recipient claims
 * one slot (single-capacity globally — first to claim books it).
 *
 * Backend wiring (agent-side, authed):
 *   GET   /api/availability?projectId=
 *   POST  /api/availability
 *   GET   /api/availability/:blastId
 *   POST  /api/availability/:blastId/close
 *   POST  /api/availability/:blastId/nudge
 *
 * The public recipient claim surface (/api/public/availability/:token) is
 * consumed directly via fetch on the unauthenticated claim page, not here.
 */

export interface AvailabilityWindow {
  date: string; // 'YYYY-MM-DD'
  start: string; // 'HH:MM'
  end: string; // 'HH:MM'
}

export interface BlastRecipient {
  id: string;
  personId: string | null;
  propertyId: string | null;
  email: string | null;
  name: string | null;
  status: "PENDING" | "CLAIMED" | "DECLINED";
  claimedSlotStart: string | null;
  claimedEventId: string | null;
  /** A time this recipient suggested, awaiting the agent's confirm/decline. */
  proposedSlotStart: string | null;
}

export interface AvailabilityBlast {
  id: string;
  /** Null for standalone blasts (Tours hub) — can be backfilled via link-project. */
  projectId: string | null;
  ownerId: string;
  bundleId: string;
  label: string | null;
  note: string | null;
  windows: AvailabilityWindow[];
  slotLengthMin: number;
  status: "OPEN" | "CLOSED";
  createdAt: string;
  claimedCount: number;
  recipientCount: number;
  recipients: BlastRecipient[];
}

export interface CreateBlastInput {
  /** Optional: omit/null for a standalone blast with no deal. */
  projectId?: string | null;
  label?: string;
  note?: string;
  windows: AvailabilityWindow[];
  slotLengthMin: number;
  recipients: Array<{
    personId?: string | null;
    propertyId?: string | null;
    email?: string | null;
    name?: string | null;
  }>;
}

export const availabilityApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "availabilityApi",
    baseUrl: "/api/availability",
  }),
  reducerPath: "availabilityApi",
  tagTypes: ["Blasts"],
  endpoints: (builder) => ({
    getBlasts: builder.query<{ data: AvailabilityBlast[] }, { projectId: string }>({
      query: ({ projectId }) => `?projectId=${encodeURIComponent(projectId)}`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Blasts" }],
    }),
    /**
     * Every ACTIVE blast the current user owns — deal-scoped AND standalone
     * (no projectId param). Powers the Tours hub so a freshly-sent blast
     * surfaces before any slot is claimed. Shares the "Blasts" tag, so
     * createBlast/updateBlast/closeBlast auto-refresh it.
     */
    getMyBlasts: builder.query<{ data: AvailabilityBlast[] }, void>({
      query: () => "/",
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Blasts" }],
    }),
    getBlast: builder.query<{ data: AvailabilityBlast }, { blastId: string }>({
      query: ({ blastId }) => `/${blastId}`,
      providesTags: [{ type: "Blasts" }],
    }),
    createBlast: builder.mutation<{ data: AvailabilityBlast }, { data: CreateBlastInput }>({
      query: ({ data }) => ({ url: "/", method: "POST", body: data }),
      invalidatesTags: [{ type: "Blasts" }],
    }),
    updateBlast: builder.mutation<
      { data: AvailabilityBlast },
      { blastId: string; data: Partial<CreateBlastInput> }
    >({
      query: ({ blastId, data }) => ({ url: `/${blastId}`, method: "PATCH", body: data }),
      invalidatesTags: [{ type: "Blasts" }],
    }),
    closeBlast: builder.mutation<{ status?: string }, { blastId: string }>({
      query: ({ blastId }) => ({ url: `/${blastId}/close`, method: "POST" }),
      invalidatesTags: [{ type: "Blasts" }],
    }),
    /** Backfill a standalone blast onto a deal (attaches its claimed tours too). */
    linkBlastProject: builder.mutation<
      { data: AvailabilityBlast },
      { blastId: string; projectId: string }
    >({
      query: ({ blastId, projectId }) => ({
        url: `/${blastId}/link-project`,
        method: "POST",
        body: { projectId },
      }),
      invalidatesTags: [{ type: "Blasts" }],
    }),
    confirmProposal: builder.mutation<
      { status?: string },
      { blastId: string; recipientId: string }
    >({
      query: ({ blastId, recipientId }) => ({
        url: `/${blastId}/recipients/${recipientId}/confirm`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Blasts" }],
    }),
    declineProposal: builder.mutation<
      { status?: string },
      { blastId: string; recipientId: string }
    >({
      query: ({ blastId, recipientId }) => ({
        url: `/${blastId}/recipients/${recipientId}/decline-proposal`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Blasts" }],
    }),
    nudgeBlast: builder.mutation<
      { status?: string; sent?: number },
      { blastId: string; recipientId?: string }
    >({
      query: ({ blastId, recipientId }) => ({
        url: `/${blastId}/nudge`,
        method: "POST",
        body: recipientId ? { recipientId } : {},
      }),
    }),
  }),
});

export const {
  useGetBlastsQuery,
  useGetMyBlastsQuery,
  useGetBlastQuery,
  useCreateBlastMutation,
  useUpdateBlastMutation,
  useCloseBlastMutation,
  useLinkBlastProjectMutation,
  useNudgeBlastMutation,
  useConfirmProposalMutation,
  useDeclineProposalMutation,
} = availabilityApi;
